import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Enhanced Language Switcher Component
 * Allows users to switch between English, French, and Spanish
 * Features flag icons, improved animations, and gaming-inspired design elements
 */
const LanguageSwitcher = () => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // Available languages with their display names and flag codes
  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'es', name: 'Español', flag: '🇪🇸' }
  ];
  
  // Get current language
  const getCurrentLanguage = () => {
    return languages.find(lang => lang.code === i18n.language) || languages[0];
  };
  
  // Handle language change
  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    setIsOpen(false);
  };
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Current language
  const currentLang = getCurrentLanguage();
  
  return (
    <div className="relative" ref={dropdownRef}>
      {/* Language selector button with globe icon and current language */}
      <button
        className="flex items-center space-x-2 text-white/90 hover:text-accent transition-all duration-300 
                  px-3 py-1.5 rounded-md hover:bg-accent/10 group"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t('languageSwitcher.language')}
      >
        {/* Globe icon */}
        <svg 
          className="w-4 h-4 text-accent/80 group-hover:text-accent transition-colors duration-300" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z">
          </path>
        </svg>
        
        {/* Current language with flag */}
        <div className="flex items-center space-x-1.5">
          <span className="text-lg leading-none">{currentLang.flag}</span>
          <span className="font-medium">{currentLang.name}</span>
        </div>
        
        {/* Dropdown arrow */}
        <svg 
          className={`w-3.5 h-3.5 transition-transform duration-300 ease-in-out ${isOpen ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
        </svg>
      </button>
      
      {/* Dropdown menu with animation */}
      <div 
        className={`absolute right-0 mt-2 w-48 bg-primary border border-accent/20 rounded-md shadow-lg z-50
                   transform transition-all duration-200 origin-top-right
                   ${isOpen 
                     ? 'opacity-100 scale-100' 
                     : 'opacity-0 scale-95 pointer-events-none'}`}
      >
        {/* Gaming-inspired accent line */}
        <div className="absolute top-0 left-0 w-full h-0.5 bg-accent"></div>
        
        <ul className="py-1">
          {languages.map((lang) => (
            <li key={lang.code}>
              <button
                className={`flex items-center w-full text-left px-4 py-2.5 text-sm transition-all duration-200
                          ${i18n.language === lang.code 
                            ? 'bg-accent/10 text-accent font-medium' 
                            : 'text-white/90 hover:bg-primary-light hover:text-white'
                          }`}
                onClick={() => changeLanguage(lang.code)}
              >
                {/* Flag icon */}
                <span className="text-lg mr-3 leading-none">{lang.flag}</span>
                
                {/* Language name */}
                <span>{lang.name}</span>
                
                {/* Active indicator */}
                {i18n.language === lang.code && (
                  <svg 
                    className="w-4 h-4 ml-auto text-accent" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                )}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default LanguageSwitcher;
